import { lazy, Suspense } from 'react'
import { Spin } from 'antd'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import RequireAuth from '@/shared/guards/RequireAuth'
import RequirePermission from '@/shared/guards/RequirePermission'
import AppLayout from './layout/AppLayout'
import LoginPage from '@/pages/LoginPage'
import SessionExpiredPage from '@/pages/SessionExpiredPage'
import DashboardPage from '@/pages/DashboardPage'
import UnauthorizedPage from '@/pages/UnauthorizedPage'
import NotFoundPage from '@/pages/NotFoundPage'
import ForbiddenPage from '@/pages/ForbiddenPage'
// Masters
import CompaniesPage from '@/pages/masters/CompaniesPage'
import PlantsPage from '@/pages/masters/PlantsPage'
// Phase 0063 — Universal Numbering Series Master
import NumberingSeriesPage from '@/pages/masters/NumberingSeriesPage'
// Sprint 0063 — Edit Approval Rules admin (closes the orphan-request gap)
import EditApprovalRulesPage from '@/pages/masters/EditApprovalRulesPage'
import DepartmentsPage from '@/pages/masters/DepartmentsPage'
import MachinesPage from '@/pages/masters/MachinesPage'
import ShiftsPage from '@/pages/masters/ShiftsPage'
import CountsPage from '@/pages/masters/CountsPage'
import SupplierCategoriesPage from '@/pages/masters/SupplierCategoriesPage'
import ItemCategoriesPage from '@/pages/masters/ItemCategoriesPage'
// Admin
import UsersPage from '@/pages/admin/UsersPage'
import RolesPage from '@/pages/admin/RolesPage'
import ApprovalMatrixAdminPage from '@/pages/admin/ApprovalMatrixAdminPage'
import ApprovalRequestsAdminPage from '@/pages/admin/ApprovalRequestsAdminPage'
import AuditLogPage from '@/pages/admin/AuditLogPage'
// Sprint 2 — Gate Inward
import GateDashboardPage from '@/pages/gate/GateDashboardPage'
import SuppliersPage from '@/pages/gate/SuppliersPage'
import ItemsPage from '@/pages/gate/ItemsPage'
import StoresPage from '@/pages/gate/StoresPage'
import GateEntryPage from '@/pages/gate/GateEntryPage'
import WeighbridgeCorrectionsPage from '@/pages/gate/WeighbridgeCorrectionsPage'
import BusinessCardLibraryPage from '@/pages/gate/BusinessCardLibraryPage'
import VisitorApprovalsPage from '@/pages/gate/VisitorApprovalsPage'
import VisitorLogPage from '@/pages/gate/VisitorLogPage'
import ExpectedInwardPage from '@/pages/gate/ExpectedInwardPage'
// Sprint 3 — Quality, Production, CAPA, Reports
import QualityTestPage from '@/pages/quality/QualityTestPage'
// QA Fix 4 — code-split: the 12 heaviest pages (>900 lines each) lazy-load
// to keep the initial bundle small. ~32k lines deferred ≈ ~600KB saved off
// first paint. Suspense fallback is wired into the rp/rpAny helpers below.
const ParakhQCPage = lazy(() => import('@/pages/quality/ParakhQCPage'))
import GRNLabResultsPage from '@/pages/quality/GRNLabResultsPage'
const RMQualityPage = lazy(() => import('@/pages/quality/RMQualityPage'))
import StockAndDwellPage from '@/pages/quality/StockAndDwellPage'
import ProductionLogPage from '@/pages/production/ProductionLogPage'
// Sprint 0074 — Layer 2 daily-entry forms (BR/CD × Production/Maint/QC)
import BlowRoomProductionPage from '@/pages/spinning/BlowRoomProductionPage'
import BlowRoomMaintenancePage from '@/pages/spinning/BlowRoomMaintenancePage'
import BlowRoomQCPage from '@/pages/spinning/BlowRoomQCPage'
import CardingProductionPage from '@/pages/spinning/CardingProductionPage'
import CardingMaintenancePage from '@/pages/spinning/CardingMaintenancePage'
import CardingQCPage from '@/pages/spinning/CardingQCPage'
import CAPAPage from '@/pages/capa/CAPAPage'
import GateReportsPage from '@/pages/reports/GateReportsPage'
import QualityReportsPage from '@/pages/reports/QualityReportsPage'
import ProductionReportsPage from '@/pages/reports/ProductionReportsPage'
import KPIDashboardPage from '@/pages/reports/KPIDashboardPage'
// Fleet & Vehicles Phase 1-3
const VehicleListPage = lazy(() => import('@/pages/fleet/VehicleListPage'))
const GateMovementPage = lazy(() => import('@/pages/fleet/GateMovementPage'))
const FuelLogPage = lazy(() => import('@/pages/fleet/FuelLogPage'))
const ServiceMaintenancePage = lazy(() => import('@/pages/fleet/ServiceMaintenancePage'))
const TyresPage = lazy(() => import('@/pages/fleet/TyresPage'))
const DamageRegisterPage = lazy(() => import('@/pages/fleet/DamageRegisterPage'))
const DocumentsPage = lazy(() => import('@/pages/fleet/DocumentsPage'))
const DriversPage = lazy(() => import('@/pages/fleet/DriversPage'))
const CostFinesPage = lazy(() => import('@/pages/fleet/CostFinesPage'))
// Sprint 4 — Procurement, Stores, Mixing
const PurchaseOrderPage = lazy(() => import('@/pages/procurement/PurchaseOrderPage'))
const DeliveryOrderPage = lazy(() => import('@/pages/procurement/DeliveryOrderPage'))
const DoReconciliationPage = lazy(() => import('@/pages/procurement/DoReconciliationPage'))
const GRNPage = lazy(() => import('@/pages/stores/GRNPage'))
const BaleManagementPage = lazy(() => import('@/pages/stores/BaleManagementPage'))
const StockLedgerPage = lazy(() => import('@/pages/stores/StockLedgerPage'))
// SLM-001 A — Inventory Location Management (bin tree, locate/scan, cycle count)
const LocationsPage = lazy(() => import('@/pages/stores/LocationsPage'))
const LocateItemPage = lazy(() => import('@/pages/stores/LocateItemPage'))
const CycleCountPage = lazy(() => import('@/pages/stores/CycleCountPage'))
// SLM-001 B — Item Labels (print) + Scan to Issue
const ItemLabelsPage = lazy(() => import('@/pages/stores/ItemLabelsPage'))
const ScanIssuePage = lazy(() => import('@/pages/stores/ScanIssuePage'))
// SLM-001 C — Consumption reporting (machine consumption + abnormality watchlist)
const MachineConsumptionPage = lazy(() => import('@/pages/stores/MachineConsumptionPage'))
const AbnormalityWatchlistPage = lazy(() => import('@/pages/stores/AbnormalityWatchlistPage'))
import MixingPlanPage from '@/pages/production/MixingPlanPage'
// Phase 0056 — 3-option picker (lives under Bale Management → Operations
// because QC owns the recipe decision step, before GM approves on Mixing Plan)
import MixRecipePickerPage from '@/pages/baleManagement/MixRecipePickerPage'
// Phase 1A 0058 — Productivity Calculator (GPS / shift kg / reverse calcs)
import ProductivityCalculatorPage from '@/pages/production/ProductivityCalculatorPage'
// Phase 2 0059 — Stock-Build / Idle-Run authorisation (MTS workflow)
import StockBuildAuthPage from '@/pages/sales/StockBuildAuthPage'
// Phase 3 0060 — Production Campaigns (multi-machine, multi-day, SO-linked)
import ProductionCampaignPage from '@/pages/production/ProductionCampaignPage'
// Sprint 6 — Production Pipeline
import ProductionPlanPage from '@/pages/production/ProductionPlanPage'
import IDTPage from '@/pages/production/IDTPage'
import SectionStockPage from '@/pages/production/SectionStockPage'
// PROD-002D Task 8 — Daily Package Weight (bobbin/cop/cone override entry)
const DailyPackageWeightPage = lazy(() => import('@/pages/production/DailyPackageWeightPage'))
// PROD-001 Phase A — Sales Order Progress
import SalesOrderProgressPage from '@/pages/production/SalesOrderProgressPage'
// PROD-001 Phase B — Carding LMW settings + alerts inbox
import CardingSettingsPage from '@/pages/production/CardingSettingsPage'
import CardingAlertsPage from '@/pages/production/CardingAlertsPage'
import CardingWasteStudiesPage from '@/pages/production/CardingWasteStudiesPage'
// Sprint 0143 — Preparatory standards (Option B: separate page per machine type)
import BreakerDFSettingsPage from '@/pages/production/BreakerDFSettingsPage'
import FinisherDFSettingsPage from '@/pages/production/FinisherDFSettingsPage'
import LapFormerSettingsPage from '@/pages/production/LapFormerSettingsPage'
import ComberSettingsPage from '@/pages/production/ComberSettingsPage'
// Sprint 0143 — Production MIS dashboard (PREP-12)
import ProductionMISPage from '@/pages/production/ProductionMISPage'
// Sprint 0143 — Production Reports + MIS exports (PREP-14)
import ProductionReportsExportsPage from '@/pages/production/ProductionReportsPage'
// Sprint 0146 QE — Carding Quick Entry from LMW display
import CardingQuickEntryPage from '@/pages/production/CardingQuickEntryPage'
// Sprint 0146 QE — Prep stages Quick Entry (4 thin wrappers around shared component)
import BreakerDFQuickEntryPage from '@/pages/production/BreakerDFQuickEntryPage'
import FinisherDFQuickEntryPage from '@/pages/production/FinisherDFQuickEntryPage'
import LapFormerQuickEntryPage from '@/pages/production/LapFormerQuickEntryPage'
import ComberQuickEntryPage from '@/pages/production/ComberQuickEntryPage'
// PROD-001A — Auto-draft daily shift log + checkpoint frequency master
import DailyShiftLogPage from '@/pages/production/DailyShiftLogPage'
import CheckpointFrequencyPage from '@/pages/production/CheckpointFrequencyPage'
// Sprint 6 — FG Store
import FGStorePage from '@/pages/fg/FGStorePage'
// Sprint 7 — Sales & Dispatch
import CustomerPage from '@/pages/sales/CustomerPage'
const SalesOrderPage = lazy(() => import('@/pages/sales/SalesOrderPage'))
import DispatchPage from '@/pages/sales/DispatchPage'
import InvoicePage from '@/pages/sales/InvoicePage'
// Sprint 5 — Security
import ContractorPage from '@/pages/security/ContractorPage'
import ContractorLaborPage from '@/pages/security/ContractorLaborPage'
import MaterialOutwardPage from '@/pages/security/MaterialOutwardPage'
import IncidentPage from '@/pages/security/IncidentPage'
import PatrolPage from '@/pages/security/PatrolPage'
import KeyControlPage from '@/pages/security/KeyControlPage'
import ShiftHandoverPage from '@/pages/security/ShiftHandoverPage'
import ScrapDisposalPage from '@/pages/security/ScrapDisposalPage'
import CCTVLogPage from '@/pages/security/CCTVLogPage'
// Sprint 9 — HR & Payroll
import EmployeesPage from '@/pages/hr/EmployeesPage'
import LeaveTypesPage from '@/pages/hr/LeaveTypesPage'
import LeaveBalancesPage from '@/pages/hr/LeaveBalancesPage'
import LeaveApplicationsPage from '@/pages/hr/LeaveApplicationsPage'
import ShiftRosterPage from '@/pages/hr/ShiftRosterPage'
import AttendancePage from '@/pages/hr/AttendancePage'
import PayrollPeriodPage from '@/pages/payroll/PayrollPeriodPage'
import PayslipPage from '@/pages/payroll/PayslipPage'
// Sprint 10 — Purchase Department
import ProcurementDashboardPage from '@/pages/procurement/ProcurementDashboardPage'
import ServiceMasterPage from '@/pages/procurement/ServiceMasterPage'
import ApprovalMatrixPage from '@/pages/procurement/ApprovalMatrixPage'
import PurchaseRequisitionPage from '@/pages/procurement/PurchaseRequisitionPage'
import RFQPage from '@/pages/procurement/RFQPage'
import QuotationEntryPage from '@/pages/procurement/QuotationEntryPage'
import ComparativeStatementPage from '@/pages/procurement/ComparativeStatementPage'
import WorkOrderPage from '@/pages/procurement/WorkOrderPage'
import ShipmentAdvicePage from '@/pages/procurement/ShipmentAdvicePage'
import ServiceEntryPage from '@/pages/procurement/ServiceEntryPage'
import RejectionNotePage from '@/pages/procurement/RejectionNotePage'
import PurchaseInvoicePage from '@/pages/procurement/PurchaseInvoicePage'
import VendorPerformancePage from '@/pages/procurement/VendorPerformancePage'
import TCTemplatesPage from '@/pages/procurement/TCTemplatesPage'
// Sprint BM — Bale Management Automation
import MillLotPage from '@/pages/baleManagement/MillLotPage'
import BaleQCPage from '@/pages/baleManagement/BaleQCPage'
const LaydownPlanningPage = lazy(() => import('@/pages/baleManagement/LaydownPlanningPage'))
// Phase BM2 — Operator-mode Quick Laydown (tablet-first, 3-tap flow)
import QuickLaydownPage from '@/pages/baleManagement/QuickLaydownPage'
// Phase 2 — Quality Posture Dashboard (rolling-window aggregation)
import QualityPostureDashboard from '@/pages/baleManagement/QualityPostureDashboard'
// Phase 2 — Floor Layout swimlanes (group by supplier + quality_group)
import FloorLayoutPage from '@/pages/baleManagement/FloorLayoutPage'
// Phase 2 — Laydown Comparison (twin run picker + property diff table)
import LaydownComparisonPage from '@/pages/baleManagement/LaydownComparisonPage'
// Phase 2 Sprint B — Cotton Grade Master (E1/E2/A/B/C/D/R)
import CottonGradeMasterPage from '@/pages/baleManagement/CottonGradeMasterPage'
// Phase 2 Sprint C — Mixing Recipe Master (count × yarn-type → grade %)
import MixingRecipeMasterPage from '@/pages/baleManagement/MixingRecipeMasterPage'
// Phase 3A — Yarn feedback loop + lot performance + bale ageing
import YarnTestEntryPage from '@/pages/baleManagement/YarnTestEntryPage'
import LotPerformancePage from '@/pages/baleManagement/LotPerformancePage'
import BaleAgeingPage from '@/pages/baleManagement/BaleAgeingPage'
// Phase 2 polish — Tagline strip rendered at the top of every Bale Mgmt page
import { withBaleMgmtFrame as bm } from '@/shared/components/BaleMgmtTagline'
const LaydownExecutionPage = lazy(() => import('@/pages/baleManagement/LaydownExecutionPage'))
// Sprint Costing — closes the prediction → consumption → FG cost loop
import DailyMixingCostPage from '@/pages/baleManagement/DailyMixingCostPage'
import CostVariancePage from '@/pages/baleManagement/CostVariancePage'
import PendingReweighPage from '@/pages/baleManagement/PendingReweighPage'
import FloorChartPage from '@/pages/baleManagement/FloorChartPage'
import BalesRunoutReportPage from '@/pages/baleManagement/BalesRunoutReportPage'
// Sprint 0072 — issued vs consumed mass-balance closure for completed laydowns.
import LaydownReconciliationPage from '@/pages/baleManagement/LaydownReconciliationPage'
import ChuteFeedPage from '@/pages/baleManagement/ChuteFeedPage'
import PreConditioningPage from '@/pages/baleManagement/PreConditioningPage'
import YarnRecipesPage from '@/pages/baleManagement/YarnRecipesPage'
import WasteCategoriesPage from '@/pages/baleManagement/WasteCategoriesPage'
import WasteStockPage from '@/pages/baleManagement/WasteStockPage'
import StageCostsPage from '@/pages/baleManagement/StageCostsPage'
// Sprint 11D — Traceability & New Store/Production pages
import IssueSlipPage from '@/pages/stores/IssueSlipPage'
import MaterialReturnPage from '@/pages/stores/MaterialReturnPage'
import GodownApprovalPage from '@/pages/stores/GodownApprovalPage'
import GodownAssignmentPage from '@/pages/stores/GodownAssignmentPage'
import QuarantineZonePage from '@/pages/stores/QuarantineZonePage'
import CountStandardPage from '@/pages/production/CountStandardPage'
import WasteDisposalPage from '@/pages/production/WasteDisposalPage'
import YarnLotTracePage from '@/pages/traceability/YarnLotTracePage'
import RMFlowReportPage from '@/pages/traceability/RMFlowReportPage'
// MIS-001 Slice 1 — Executive Dashboard + Insights Reports (Daily digest)
const ExecutiveDashboardPage = lazy(() => import('@/pages/mis/ExecutiveDashboardPage'))
const InsightsReportsPage = lazy(() => import('@/pages/mis/InsightsReportsPage'))
// BI-001 Slice 1 — Custom dashboard list + viewer
const DashboardListPage = lazy(() => import('@/pages/bi/DashboardListPage'))
const DashboardViewPage = lazy(() => import('@/pages/bi/DashboardViewPage'))
const DashboardBuilderPage = lazy(() => import('@/pages/bi/DashboardBuilderPage'))
// MIS-001 Slice 2 — Stores Cockpit + Maintenance Cockpit
const StoresCockpitPage = lazy(() => import('@/pages/mis/StoresCockpitPage'))
const MaintenanceCockpitPage = lazy(() => import('@/pages/mis/MaintenanceCockpitPage'))
// Sprint 11 — MIS & Accounts Reports
import MISDashboardPage from '@/pages/mis/MISDashboardPage'
import ProfitLossPage from '@/pages/mis/ProfitLossPage'
import BalanceSheetPage from '@/pages/mis/BalanceSheetPage'
import DebtorAgingPage from '@/pages/mis/DebtorAgingPage'
import CreditorAgingPage from '@/pages/mis/CreditorAgingPage'
import CostOfProductionPage from '@/pages/mis/CostOfProductionPage'
import BudgetPage from '@/pages/mis/BudgetPage'
import CashFlowPage from '@/pages/mis/CashFlowPage'
import LandedCostPage from '@/pages/mis/LandedCostPage'
// GOV-001 P4 — cost rate change approval
import CostRateChangeRequestsPage from '@/pages/costing/CostRateChangeRequestsPage'
// Accounting
import AccountingDashboardPage from '@/pages/accounting/AccountingDashboardPage'
import ChartOfAccountsPage from '@/pages/accounting/ChartOfAccountsPage'
import VouchersPage from '@/pages/accounting/VouchersPage'
import GSTRegisterPage from '@/pages/accounting/GSTRegisterPage'
import TDSRegisterPage from '@/pages/accounting/TDSRegisterPage'
import BankReconciliationPage from '@/pages/accounting/BankReconciliationPage'
import FixedAssetsPage from '@/pages/accounting/FixedAssetsPage'
import ComplianceDashboardPage from '@/pages/accounting/ComplianceDashboardPage'
import TrialBalancePage from '@/pages/accounting/TrialBalancePage'
import MonthlyClosePage from '@/pages/accounting/MonthlyClosePage'
import StandardCostPage from '@/pages/accounting/StandardCostPage'
import AuditSchedulesPage from '@/pages/accounting/AuditSchedulesPage'
const APInvoiceBookingPage = lazy(() => import('@/pages/accounting/APInvoiceBookingPage'))
import TallyImportPage from '@/pages/accounting/TallyImportPage'
import BillWisePage from '@/pages/accounting/BillWisePage'
import FinancialReportsPage from '@/pages/accounting/FinancialReportsPage'
import FinancialStatementsPage from '@/pages/accounting/FinancialStatementsPage'
import PaymentAllocationPage from '@/pages/accounting/PaymentAllocationPage'
// Edit-Lock / Approvals
import ApprovalInboxPage from '@/pages/approvals/ApprovalInboxPage'
import MyPendingApprovalsPage from '@/pages/approvals/MyPendingApprovalsPage'
import VendorApprovalInboxPage from '@/pages/approvals/VendorApprovalInboxPage'
// Sprint 0075 Phase 2 — Maintenance role-wise screens
import MaintenanceHomePage from '@/pages/maintenance/MaintenanceHomePage'
import BreakdownRequestPage from '@/pages/maintenance/BreakdownRequestPage'
import WorkOrderBoardPage from '@/pages/maintenance/WorkOrderBoardPage'
import TechnicianMyJobsPage from '@/pages/maintenance/TechnicianMyJobsPage'
import SpareIssuePage from '@/pages/maintenance/SpareIssuePage'
import PMCalendarPage from '@/pages/maintenance/PMCalendarPage'
import ShutdownPlanPage from '@/pages/maintenance/ShutdownPlanPage'
import UtilityRunningLogPage from '@/pages/maintenance/UtilityRunningLogPage'
import MachineHealthPage from '@/pages/maintenance/MachineHealthPage'
import MaintenanceMastersPage from '@/pages/maintenance/MaintenanceMastersPage'
// Sprint 0075 Phase 3 — Maintenance dashboards + reports
import MaintenanceManagerDashboardPage from '@/pages/maintenance/MaintenanceManagerDashboardPage'
import PlantHeadMaintenanceDashboardPage from '@/pages/maintenance/PlantHeadMaintenanceDashboardPage'
import MaintenanceReportsPage from '@/pages/maintenance/MaintenanceReportsPage'
import MachineDowntimeReportPage from '@/pages/maintenance/MachineDowntimeReportPage'
import DepartmentBreakdownReportPage from '@/pages/maintenance/DepartmentBreakdownReportPage'
import PMComplianceReportPage from '@/pages/maintenance/PMComplianceReportPage'
import SpareConsumptionReportPage from '@/pages/maintenance/SpareConsumptionReportPage'
import TechnicianPerformanceReportPage from '@/pages/maintenance/TechnicianPerformanceReportPage'
import MaintenanceCostReportPage from '@/pages/maintenance/MaintenanceCostReportPage'
import RepeatBreakdownReportPage from '@/pages/maintenance/RepeatBreakdownReportPage'
import PendingWorkOrderAgeingReportPage from '@/pages/maintenance/PendingWorkOrderAgeingReportPage'
import UtilityPerformanceReportPage from '@/pages/maintenance/UtilityPerformanceReportPage'
import MachineHealthAnalyticsPage from '@/pages/maintenance/MachineHealthAnalyticsPage'
// Sprint 0075 Phase 3.1 — Maintenance Cost per kg trend
import MaintenanceCostPerKgTrendPage from '@/pages/maintenance/MaintenanceCostPerKgTrendPage'
// Sprint 0078 — Auto-assignment rules
import AssignmentRulesPage from '@/pages/maintenance/AssignmentRulesPage'
// Sprint 0081 — HR Skill / Trade Matrix
import SkillMatrixPage from '@/pages/hr/SkillMatrixPage'
// Sprint 0082 — Manpower allocation chain
import SanctionedStrengthPage from '@/pages/hr/SanctionedStrengthPage'
import ManpowerNormsPage from '@/pages/hr/ManpowerNormsPage'
const WorkerAllocationPage = lazy(() => import('@/pages/hr/WorkerAllocationPage'))
// Sprint 0144 ATT — GM approvals for no-gate-log allocation overrides
const AllocationGMApprovalsPage = lazy(() => import('@/pages/hr/AllocationGMApprovalsPage'))
// HR-004 follow-up — admin-editable worker role master
import WorkerRoleMasterPage from '@/pages/hr/WorkerRoleMasterPage'
// Sprint 0083 — HR config + completeness + punch UI + rule config
import ConfigCompletenessPage from '@/pages/hr/ConfigCompletenessPage'
import PunchLogsPage from '@/pages/hr/PunchLogsPage'
import DailyAttendanceReportPage from '@/pages/hr/DailyAttendanceReportPage'
import RuleConfigPage from '@/pages/hr/RuleConfigPage'
// Phase 0084 — Role Responsibility Master + Org Chart
import RoleMasterListPage from '@/pages/hr/RoleMasterListPage'
import RoleDetailPage from '@/pages/hr/RoleDetailPage'
import OrgChartPage from '@/pages/hr/OrgChartPage'
import ReportingHierarchyAuditPage from '@/pages/hr/ReportingHierarchyAuditPage'
// Phase 0085 — Task execution + scoring + awards
import MyTodoPage from '@/pages/hr/MyTodoPage'
import TeamMonitorPage from '@/pages/hr/TeamMonitorPage'
import TaskVerificationPage from '@/pages/hr/TaskVerificationPage'
import EscalationInboxPage from '@/pages/hr/EscalationInboxPage'
import GrievanceInboxPage from '@/pages/hr/GrievanceInboxPage'
import WorkerEfficiencyPage from '@/pages/hr/WorkerEfficiencyPage'
import SafetyWalkPage from '@/pages/hr/SafetyWalkPage'
import TrainingPage from '@/pages/hr/TrainingPage'
import ProductionWalkPage from '@/pages/hr/ProductionWalkPage'
import WelfarePage from '@/pages/hr/WelfarePage'
import AttendanceReconciliationPage from '@/pages/hr/AttendanceReconciliationPage'
import TaskCalendarPage from '@/pages/hr/TaskCalendarPage'
import AwardsPage from '@/pages/hr/AwardsPage'
// Phase 0085-B — Assessment engine
import AssessmentTemplatesPage from '@/pages/hr/AssessmentTemplatesPage'
import AssessmentCyclesPage from '@/pages/hr/AssessmentCyclesPage'
import MyAssessmentsPage from '@/pages/hr/MyAssessmentsPage'
import AssessmentDetailPage from '@/pages/hr/AssessmentDetailPage'
// Phase 0088 — Scheduler admin
import SchedulerAdminPage from '@/pages/hr/SchedulerAdminPage'
// Phase 0089 — Live shop-floor studies
import LiveStudyHomePage from '@/pages/hr/LiveStudyHomePage'
import StartStudyWizardPage from '@/pages/hr/StartStudyWizardPage'
import LiveStudyHistoryPage from '@/pages/hr/LiveStudyHistoryPage'
// Phase 0093-A — capture-mode dispatcher + session recovery
// (LiveStudyCapturePage is now imported by LiveStudyCaptureRouter, not directly.)
import LiveStudyCaptureRouter from '@/pages/hr/LiveStudyCaptureRouter'
// Phase 0091 — Automation control surface
import HRAutomationDashboardPage from '@/pages/hr/HRAutomationDashboardPage'
// Phase 0093-B0 — HR default-config admin
import HRDefaultConfigPage from '@/pages/hr/HRDefaultConfigPage'
// Phase 0093-B1 — Study direction engine
import StudyDirectionsPage from '@/pages/hr/StudyDirectionsPage'
// Phase 0094-P1 — Payroll Foundation
import PayrollRunPage from '@/pages/payroll/PayrollRunPage'
import SalaryStructuresPage from '@/pages/payroll/SalaryStructuresPage'
import SalaryRegisterPage from '@/pages/payroll/SalaryRegisterPage'
import PayrollCostingPage from '@/pages/payroll/PayrollCostingPage'
// Phase 0094-P2 — EPF Compliance
import EPFPage from '@/pages/payroll/EPFPage'
// Phase COTTON-RM-002 Part B — Cotton ops UI
import CottonInPremisesPage from '@/pages/inventory/CottonInPremisesPage'
import CottonMovementPage from '@/pages/inventory/CottonMovementPage'
import MoisturePage from '@/pages/quality/MoisturePage'
import BaleQrPrintPage from '@/pages/inventory/BaleQrPrintPage'
import BaleScanPage from '@/pages/inventory/BaleScanPage'
import BaleTraceabilityPage from '@/pages/inventory/BaleTraceabilityPage'
import CottonCostAdjustmentsPage from '@/pages/inventory/CottonCostAdjustmentsPage'
// Phase COTTON-RM-002 Part C — Cotton RM control surface
import CottonRmDashboardPage from '@/pages/inventory/CottonRmDashboardPage'
import CottonQcPendingPage from '@/pages/quality/CottonQcPendingPage'
import MixingReadinessReportPage from '@/pages/inventory/MixingReadinessReportPage'
import CottonCostAdjustmentReportPage from '@/pages/inventory/CottonCostAdjustmentReportPage'
import CottonCostRestatementsPage from '@/pages/inventory/CottonCostRestatementsPage'
import AttendanceDerivationPage from '@/pages/hr/AttendanceDerivationPage'
import CertificationExpiryPage from '@/pages/hr/CertificationExpiryPage'
// HR-004 follow-up — eSSL biometric devices admin + bulk employee mapping
import BiometricDevicesPage from '@/pages/hr/BiometricDevicesPage'
// Phase A — Welfare masters (colonies, canteens, ration)
import ColoniesRoomsPage from '@/pages/hr/ColoniesRoomsPage'
import CanteenRulesPage from '@/pages/hr/CanteenRulesPage'
import RationRulesPage from '@/pages/hr/RationRulesPage'
// Phase B — Welfare approvals inbox
import WelfareApprovalsInboxPage from '@/pages/hr/WelfareApprovalsInboxPage'
import RationAdvancesPage from '@/pages/hr/RationAdvancesPage'
// Phase 0092 — HR Executive Dashboard
import HRExecutiveDashboardPage from '@/pages/hr/HRExecutiveDashboardPage'
// Employee Joining Workflow
import JoiningListPage from '@/pages/hr/JoiningListPage'
import JoiningDetailPage from '@/pages/hr/JoiningDetailPage'
// PJD-001 Task 11 — Document Grace watchlist (2nd-ID chase ladder)
const DocGraceWatchlistPage = lazy(() => import('@/pages/hr/DocGraceWatchlistPage'))
// Contractor Foundation — contractor master list + detail
const ContractorsPage = lazy(() => import('@/pages/hr/ContractorsPage'))
const ContractorDetailPage = lazy(() => import('@/pages/hr/ContractorDetailPage'))
// Food Store — welfare food catalog / batches / issue / dashboard
const FoodStoreDashboardPage = lazy(() => import('@/pages/hr/foodstore/FoodStoreDashboardPage'))
const FoodItemsPage = lazy(() => import('@/pages/hr/foodstore/FoodItemsPage'))
const FoodIssuePage = lazy(() => import('@/pages/hr/foodstore/FoodIssuePage'))
const FoodBatchesPage = lazy(() => import('@/pages/hr/foodstore/FoodBatchesPage'))
const ContractorFoodLedgerPage = lazy(() => import('@/pages/hr/foodstore/ContractorFoodLedgerPage'))
// Attendance Variance Task system — HR → GM → MD escalation
import AttendanceVarianceTasksPage from '@/pages/hr/AttendanceVarianceTasksPage'
// Contractor Headcount SLA — continuous shortfall + HR → GM → MD escalation
const ContractorHeadcountSlaTasksPage = lazy(() => import('@/pages/hr/ContractorHeadcountSlaTasksPage'))
// Skill mismatch system — HR → GM → MD escalation + bulk worker classification
import SkillMismatchTasksPage from '@/pages/hr/SkillMismatchTasksPage'
import WorkerSkillsPage from '@/pages/hr/WorkerSkillsPage'
// Employee Master — tabbed profile editor for ACTIVE workers
// (reuses the joining tab components, no joining workflow chrome)
import EmployeeDetailPage from '@/pages/hr/EmployeeDetailPage'
// Separation / Resignation workflow + F&F settlement
import SeparationListPage from '@/pages/hr/SeparationListPage'
import SeparationDetailPage from '@/pages/hr/SeparationDetailPage'
// Employee ledgers — loans / canteen / colony bills (feed F&F auto-fetch)
import EmployeeLedgersPage from '@/pages/hr/EmployeeLedgersPage'
// Statutory Exit dashboard — PF Form 10 / ESI / Gratuity tracking
import StatutoryExitDashboardPage from '@/pages/hr/StatutoryExitDashboardPage'
// Phase 5+6+7 — Asset ledger / unified reports / F&F finance posting
import EmployeeAssetsPage from '@/pages/hr/EmployeeAssetsPage'
import SeparationReportsPage from '@/pages/hr/SeparationReportsPage'
import FinancePostingPage from '@/pages/hr/FinancePostingPage'
// Sprint 0076 Phase 3.2-B — Maintenance ↔ Store ↔ Purchase ↔ Accounts
import SpareProcurementDashboardPage from '@/pages/maintenance/SpareProcurementDashboardPage'
import AutoPRFromWorkOrderPage from '@/pages/maintenance/AutoPRFromWorkOrderPage'
import MaintenanceProcurementStatusPage from '@/pages/maintenance/MaintenanceProcurementStatusPage'
import SpareGRNEntryPage from '@/pages/maintenance/SpareGRNEntryPage'
import SpareInvoiceMatchPage from '@/pages/maintenance/SpareInvoiceMatchPage'
import POAdvancePaymentPage from '@/pages/maintenance/POAdvancePaymentPage'
// Sprint 0076 Phase 3.2-C — Stock ageing + slow-moving inventory
import StockAgeingDashboardPage from '@/pages/inventory/StockAgeingDashboardPage'
import AgeingReportPage from '@/pages/inventory/AgeingReportPage'
// SPR-001B — Spare Requests (store-side spare indent workflow)
const SpareRequestsPage = lazy(() => import('@/features/stores/SpareRequestsPage'))
const SpareRequestNewPage = lazy(() => import('@/features/stores/SpareRequestNewPage'))
const SpareRequestDetailPage = lazy(() => import('@/features/stores/SpareRequestDetailPage'))
// SPR-001C — Spare procurement quote-flow (purchase indents / vendor quotes / comparative statement)
// NOTE: aliased StoresComparativeStatementPage to avoid clashing with the
// procurement ComparativeStatementPage imported above.
const PurchaseIndentsPage = lazy(() => import('@/features/stores/PurchaseIndentsPage'))
const VendorQuotesPage = lazy(() => import('@/features/stores/VendorQuotesPage'))
const StoresComparativeStatementPage = lazy(() => import('@/features/stores/ComparativeStatementPage'))
const SparePurchaseOrdersPage = lazy(() => import('@/pages/stores/SparePurchaseOrdersPage'))
const RepairJobsPage = lazy(() => import('@/pages/stores/RepairJobsPage'))
const SpareLoansPage = lazy(() => import('@/pages/stores/SpareLoansPage'))
const StoresSparesMisPage = lazy(() => import('@/pages/stores/StoresSparesMisPage'))
const MonthlyBudgetPage = lazy(() => import('@/pages/stores/MonthlyBudgetPage'))
const ExtraBudgetRequestsPage = lazy(() => import('@/pages/stores/ExtraBudgetRequestsPage'))
// SPARE-REORDER-001 — Auto-reorder recommendation review + config
const ReorderRecommendationsPage = lazy(() => import('@/pages/stores/ReorderRecommendationsPage'))
// SPARES-PROC-SEP-001 Tasks 6+7 — Spare Master browse (store keeper) + Spare Buying Queue (buyer)
import StoresSpareMasterPage from '@/pages/stores/StoresSpareMasterPage'
import SpareBuyingQueuePage from '@/pages/stores/SpareBuyingQueuePage'

// ── Suspense fallback for lazy-loaded pages ────────────────────────────────────
// Lightweight centered spinner — only ever shown for the ~1–2s a chunk takes
// to fetch on first nav to a code-split page (e.g. RMQuality, BaleManagement).
const LazyFallback = () => (
  <div style={{
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    minHeight: 320,
  }}>
    <Spin size="large" />
  </div>
)

// ── Helper: wrap element with permission guard + Suspense ──────────────────────
const rp = (perm: string, el: JSX.Element) => (
  <RequirePermission permission={perm}>
    <Suspense fallback={<LazyFallback />}>{el}</Suspense>
  </RequirePermission>
)
const rpAny = (perms: string[], el: JSX.Element) => (
  <RequirePermission anyOf={perms}>
    <Suspense fallback={<LazyFallback />}>{el}</Suspense>
  </RequirePermission>
)

const router = createBrowserRouter([
  { path: '/login',           element: <LoginPage /> },
  { path: '/unauthorized',    element: <UnauthorizedPage /> },
  { path: '/403',             element: <ForbiddenPage /> },
  { path: '/session-expired', element: <SessionExpiredPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },

      // ── Masters ────────────────────────────────────────────────────────────
      { path: 'masters/numbering-series', element: rp('user.read',  <NumberingSeriesPage />) },
      { path: 'masters/approval-rules',   element: rp('user.read',  <EditApprovalRulesPage />) },
      { path: 'masters/companies',   element: rp('company.read',    <CompaniesPage />) },
      { path: 'masters/plants',      element: rp('plant.read',      <PlantsPage />) },
      { path: 'masters/departments', element: rp('department.read', <DepartmentsPage />) },
      { path: 'masters/machines',    element: rp('machine.read',    <MachinesPage />) },
      // Shift Master — system-wide working shifts (HR + Production + QA use)
      { path: 'masters/shifts',      element: rp('shift.read',      <ShiftsPage />) },
      { path: 'masters/counts',      element: rp('count.read',      <CountsPage />) },
      { path: 'masters/supplier-categories', element: rp('supplier.read', <SupplierCategoriesPage />) },
      { path: 'masters/item-categories',     element: rp('item.read',     <ItemCategoriesPage />) },

      // ── Admin ──────────────────────────────────────────────────────────────
      { path: 'admin/users', element: rp('user.read',  <UsersPage />) },
      { path: 'admin/roles', element: rp('role.read',  <RolesPage />) },
      { path: 'admin/approval-matrix', element: rp('approval_matrix.read', <ApprovalMatrixAdminPage />) },
      { path: 'admin/approval-requests', element: rp('approval_matrix.read', <ApprovalRequestsAdminPage />) },
      { path: 'admin/audit', element: rp('audit.read', <AuditLogPage />) },

      // ── Gate & Security ────────────────────────────────────────────────────
      { path: 'gate/dashboard',            element: rp('gate.read',     <GateDashboardPage />) },
      { path: 'gate/suppliers',            element: rp('supplier.read', <SuppliersPage />) },
      { path: 'gate/items',                element: rp('item.read',     <ItemsPage />) },
      { path: 'gate/stores',               element: rp('store.read',    <StoresPage />) },
      { path: 'gate/entries',              element: rp('gate.read',     <GateEntryPage />) },
      { path: 'gate/weighbridge-corrections', element: rp('weighbridge.read', <WeighbridgeCorrectionsPage />) },
      { path: 'gate/business-card-library',    element: rp('visitor.read',  <BusinessCardLibraryPage />) },
      { path: 'gate/visitor-approvals',        element: rp('visitor.approve', <VisitorApprovalsPage />) },
      { path: 'gate/visitors',             element: rp('visitor.read',  <VisitorLogPage />) },
      { path: 'gate/expected-inward',      element: rp('gate.read',     <ExpectedInwardPage />) },
      { path: 'security/contractors',      element: rp('gate.read',     <ContractorPage />) },
      { path: 'security/contractor-labor', element: rp('gate.read',     <ContractorLaborPage />) },
      { path: 'security/material-outward', element: rp('gate.read',     <MaterialOutwardPage />) },
      { path: 'security/incidents',        element: rp('gate.read',     <IncidentPage />) },
      { path: 'security/patrol',           element: rp('gate.read',     <PatrolPage />) },
      { path: 'security/key-control',      element: rp('gate.read',     <KeyControlPage />) },
      { path: 'security/shift-handover',   element: rp('gate.read',     <ShiftHandoverPage />) },
      { path: 'security/scrap-disposal',   element: rp('gate.read',     <ScrapDisposalPage />) },
      { path: 'security/cctv-logs',        element: rp('gate.read',     <CCTVLogPage />) },

      // ── Quality ────────────────────────────────────────────────────────────
      { path: 'quality/rm',            element: rp('quality.read', <RMQualityPage />) },
      { path: 'quality/parakh-qc',     element: rp('quality.read', <ParakhQCPage />) },
      { path: 'quality/tests',         element: rp('quality.read', <QualityTestPage />) },
      { path: 'quality/grn-lab-results', element: rp('quality.read', <GRNLabResultsPage />) },
      { path: 'quality/stock-dwell',   element: rp('quality.read', <StockAndDwellPage />) },
      { path: 'capa/records',          element: rp('quality.read', <CAPAPage />) },

      // ── Production ─────────────────────────────────────────────────────────
      { path: 'production/logs',           element: rp('production.read', <ProductionLogPage />) },
      { path: 'production/mixing-plans',   element: rp('production.read', <MixingPlanPage />) },
      { path: 'production/campaigns',      element: rp('production.read', <ProductionCampaignPage />) },
      { path: 'production/productivity',   element: rp('production.read', <ProductivityCalculatorPage />) },
      { path: 'production/plan',           element: rp('production.read', <ProductionPlanPage />) },
      { path: 'production/idt',            element: rp('production.read', <IDTPage />) },
      { path: 'production/section-stock',  element: rp('production.read', <SectionStockPage />) },
      { path: 'production/daily-package-weight', element: rp('production.read', <DailyPackageWeightPage />) },
      { path: 'production/count-standards',element: rp('production.read', <CountStandardPage />) },
      { path: 'production/waste-disposal', element: rp('production.read', <WasteDisposalPage />) },
      // PROD-001 Phase A — Sales Order Progress dashboard
      { path: 'production/sales-order-progress', element: rp('production.read', <SalesOrderProgressPage />) },
      // PROD-001 Phase B — Carding LMW settings + alerts inbox
      { path: 'production/carding-settings', element: rp('production.read', <CardingSettingsPage />) },
      { path: 'production/carding-alerts',   element: rp('production.read', <CardingAlertsPage />) },
      // Sprint 0141 — Monthly card waste study worksheet
      { path: 'production/carding-waste-studies', element: rp('production.read', <CardingWasteStudiesPage />) },
      // Sprint 0143 — Preparatory standards (separate page per Option B)
      { path: 'production/breaker-df-settings',   element: rp('production.read', <BreakerDFSettingsPage />) },
      { path: 'production/finisher-df-settings',  element: rp('production.read', <FinisherDFSettingsPage />) },
      { path: 'production/lap-former-settings',   element: rp('production.read', <LapFormerSettingsPage />) },
      { path: 'production/comber-settings',       element: rp('production.read', <ComberSettingsPage />) },
      // Sprint 0143 — Production MIS dashboard
      { path: 'production/mis',                   element: rp('production.read', <ProductionMISPage />) },
      // Sprint 0143 PREP-14 — Production Reports + MIS Excel exports
      { path: 'production/reports',               element: rp('production.read', <ProductionReportsExportsPage />) },
      // Sprint 0146 — Carding Quick Entry from LMW display
      { path: 'production/carding-quick-entry',   element: rp('production.create', <CardingQuickEntryPage />) },
      // Sprint 0146 — Prep stages Quick Entry
      { path: 'production/breaker-df-quick-entry',  element: rp('production.create', <BreakerDFQuickEntryPage />) },
      { path: 'production/finisher-df-quick-entry', element: rp('production.create', <FinisherDFQuickEntryPage />) },
      { path: 'production/lap-former-quick-entry',  element: rp('production.create', <LapFormerQuickEntryPage />) },
      { path: 'production/comber-quick-entry',      element: rp('production.create', <ComberQuickEntryPage />) },
      // PROD-001A — Auto-draft daily shift log + checkpoint frequency master
      { path: 'production/daily-log',          element: rp('production.read', <DailyShiftLogPage />) },
      { path: 'production/checkpoint-frequency', element: rp('production.read', <CheckpointFrequencyPage />) },

      // Sprint 0074 — Daily-entry forms by department (Layer 2 spine).
      // Production forms reuse /production-logs; Maint + QC forms use
      // /checklist-logs. Bulk QC + can selection live on the QC pages.
      { path: 'spinning/blow-room/production',  element: rp('production.create', <BlowRoomProductionPage />) },
      { path: 'spinning/blow-room/maintenance', element: rp('production.create', <BlowRoomMaintenancePage />) },
      { path: 'spinning/blow-room/qc',          element: rp('quality.create', <BlowRoomQCPage />) },
      { path: 'spinning/carding/production',    element: rp('production.create', <CardingProductionPage />) },
      { path: 'spinning/carding/maintenance',   element: rp('production.create', <CardingMaintenancePage />) },
      { path: 'spinning/carding/qc',            element: rp('quality.create', <CardingQCPage />) },

      // ── Reports ────────────────────────────────────────────────────────────
      { path: 'reports/kpi',        element: rp('report.read', <KPIDashboardPage />) },
      { path: 'reports/gate',       element: rp('report.read', <GateReportsPage />) },
      { path: 'reports/quality',    element: rp('report.read', <QualityReportsPage />) },
      { path: 'reports/production', element: rp('report.read', <ProductionReportsPage />) },

      // ── Traceability ───────────────────────────────────────────────────────
      { path: 'traceability/yarn-lot',       element: rp('report.read', <YarnLotTracePage />) },
      { path: 'traceability/rm-flow-report', element: rp('report.read', <RMFlowReportPage />) },

      // ── Fleet & Vehicles ─────────────────────────────────────────────────────
      { path: 'fleet/vehicles',                 element: rp('vehicle.read',         <VehicleListPage />) },
      { path: 'fleet/gate-movement',            element: rp('vehicle.trip.read',    <GateMovementPage />) },
      { path: 'fleet/drivers',                  element: rp('vehicle.trip.read',    <DriversPage />) },
      { path: 'fleet/fuel',                     element: rp('vehicle.fuel.read',    <FuelLogPage />) },
      { path: 'fleet/service',                  element: rp('vehicle.service.read', <ServiceMaintenancePage />) },
      { path: 'fleet/tyres',                    element: rp('vehicle.tyre.read',    <TyresPage />) },
      { path: 'fleet/damage',                   element: rp('vehicle.damage.read',  <DamageRegisterPage />) },
      { path: 'fleet/documents',                element: rp('vehicle.document.read', <DocumentsPage />) },
      { path: 'fleet/cost-fines',               element: rp('vehicle.fine.read',    <CostFinesPage />) },

      // ── Procurement ────────────────────────────────────────────────────────
      { path: 'procurement/dashboard',          element: rp('po.read',              <ProcurementDashboardPage />) },
      { path: 'procurement/purchase-orders',    element: rp('po.read',              <PurchaseOrderPage />) },
      { path: 'procurement/delivery-orders',    element: rp('po.read',              <DeliveryOrderPage />) },
      { path: 'procurement/do-reconciliation',  element: rp('po.read',              <DoReconciliationPage />) },
      { path: 'procurement/requisitions',       element: rp('pr.read',              <PurchaseRequisitionPage />) },
      { path: 'procurement/rfqs',               element: rp('rfq.read',             <RFQPage />) },
      { path: 'procurement/quotations',         element: rp('quotation.read',       <QuotationEntryPage />) },
      { path: 'procurement/cs',                 element: rp('cs.read',              <ComparativeStatementPage />) },
      { path: 'procurement/work-orders',        element: rp('wo.read',              <WorkOrderPage />) },
      { path: 'procurement/shipment-advices',   element: rp('po.read',              <ShipmentAdvicePage />) },
      { path: 'procurement/service-entries',    element: rp('service_entry.read',   <ServiceEntryPage />) },
      { path: 'procurement/rejection-notes',    element: rp('rejection.read',       <RejectionNotePage />) },
      { path: 'procurement/invoices',           element: rp('purchase_invoice.read',<PurchaseInvoicePage />) },
      { path: 'procurement/vendor-performance', element: rp('vendor_perf.read',     <VendorPerformancePage />) },
      { path: 'procurement/service-masters',    element: rp('service_master.read',  <ServiceMasterPage />) },
      { path: 'procurement/approval-matrix',    element: rp('approval_matrix.read', <ApprovalMatrixPage />) },
      { path: 'procurement/tc-templates',       element: rp('po.read',              <TCTemplatesPage />) },

      // ── Stores & Stock ─────────────────────────────────────────────────────
      { path: 'stores/grn',               element: rp('grn.read',    <GRNPage />) },
      { path: 'stores/bales',             element: rp('bale.read',   <BaleManagementPage />) },
      { path: 'stores/stock-ledger',      element: rp('store.read',  <StockLedgerPage />) },
      { path: 'stores/issue-slips',       element: rp('store.read',  <IssueSlipPage />) },
      { path: 'stores/material-returns',  element: rp('store.read',  <MaterialReturnPage />) },
      { path: 'stores/godown-assignment', element: rp('godown.read', <GodownAssignmentPage />) },
      { path: 'stores/godown-approval',   element: rp('godown.read', <GodownApprovalPage />) },
      { path: 'stores/quarantine-zone',   element: rpAny(['quality.read', 'godown.read'], <QuarantineZonePage />) },
      // SPR-001B — Spare Requests: list / create / detail. `new` before `:id`
      // so it isn't captured as a request id.
      { path: 'stores/spare-requests',     element: rp('spares.read', <SpareRequestsPage />) },
      { path: 'stores/spare-requests/new', element: rp('spares.read', <SpareRequestNewPage />) },
      { path: 'stores/spare-requests/:id', element: rp('spares.read', <SpareRequestDetailPage />) },
      // SPR-001C — spare procurement quote-flow pages
      { path: 'stores/purchase-indents',        element: rp('pr.read',        <PurchaseIndentsPage />) },
      { path: 'stores/vendor-quotes',           element: rp('quotation.read', <VendorQuotesPage />) },
      { path: 'stores/comparative-statements',  element: rp('cs.read',        <StoresComparativeStatementPage />) },
      { path: 'stores/spare-purchase-orders',   element: rp('po.read',        <SparePurchaseOrdersPage />) },
      { path: 'stores/repair-jobs',             element: rp('repair.read',    <RepairJobsPage />) },
      { path: 'stores/spare-loans',             element: rp('spares.loan.read', <SpareLoansPage />) },
      { path: 'stores/spares-mis',              element: rp('budget.read',    <StoresSparesMisPage />) },
      { path: 'stores/monthly-budget',          element: rp('budget.read',    <MonthlyBudgetPage />) },
      { path: 'stores/extra-budget-requests',   element: rp('budget.read',    <ExtraBudgetRequestsPage />) },
      // SPARE-REORDER-001 — Auto-reorder recommendation review + config
      { path: 'stores/reorder',                 element: rp('spares.reorder.read', <ReorderRecommendationsPage />) },
      // SPARES-PROC-SEP-001 Tasks 6+7 — Spare Master browse + Spare Buying Queue
      { path: 'stores/spare-master',             element: rp('spares.read',    <StoresSpareMasterPage />) },
      { path: 'stores/spare-buying-queue',       element: rp('pr.read',        <SpareBuyingQueuePage />) },
      // SLM-001 A — Inventory Location Management (WMS bin tree / locate / cycle count)
      { path: 'stores/locations',   element: rp('location.read', <LocationsPage />) },
      { path: 'stores/locate',      element: rp('location.read', <LocateItemPage />) },
      { path: 'stores/cycle-count', element: rp('bin.adjust',    <CycleCountPage />) },
      // SLM-001 B — QR label printing + scan-to-issue
      { path: 'stores/scan-issue',   element: rp('spares.scan.issue',   <ScanIssuePage />) },
      { path: 'stores/item-labels',  element: rp('location.label.print', <ItemLabelsPage />) },
      // SLM-001 C — Consumption reporting (machine consumption + abnormality watchlist)
      { path: 'stores/consumption', element: rp('spare.consumption.read', <MachineConsumptionPage />) },
      { path: 'stores/abnormality', element: rp('spare.consumption.read', <AbnormalityWatchlistPage />) },

      // ── Bale Management ────────────────────────────────────────────────────
      // Every page wrapped with bm() to render the BaleMgmtTagline strip on top:
      //   "Bale management is the science of converting variable cotton into
      //    stable yarn at the lowest safe cost."
      { path: 'bale-mgmt/mill-lots',         element: bm(rp('bale.read', <MillLotPage />)) },
      { path: 'bale-mgmt/bale-qc',           element: bm(rp('bale.read', <BaleQCPage />)) },
      // Phase BM2 — operator-mode quick laydown (tablet, 3-tap)
      { path: 'bale-mgmt/quick-laydown',     element: bm(rp('bale.read', <QuickLaydownPage />)) },
      // Phase 2 — Quality Posture Dashboard
      { path: 'bale-mgmt/quality-posture',   element: bm(rp('bale.read', <QualityPostureDashboard />)) },
      // Phase 2 — Floor Layout swimlanes
      { path: 'bale-mgmt/floor-layout',      element: bm(rp('bale.read', <FloorLayoutPage />)) },
      // Phase 2 — Compare two bale-selection runs side by side
      { path: 'bale-mgmt/compare-laydowns',  element: bm(rp('bale.read', <LaydownComparisonPage />)) },
      // Phase 2 Sprint B — Cotton Grade Master CRUD
      { path: 'bale-mgmt/cotton-grades',     element: bm(rp('bale.read', <CottonGradeMasterPage />)) },
      // Phase 2 Sprint C — Mixing Recipe Master browser
      { path: 'bale-mgmt/mixing-recipes',    element: bm(rp('bale.read', <MixingRecipeMasterPage />)) },
      // Phase 3A — Yarn feedback loop + lot performance + bale ageing
      { path: 'bale-mgmt/yarn-tests',        element: bm(rp('quality.read', <YarnTestEntryPage />)) },
      { path: 'bale-mgmt/lot-performance',   element: bm(rp('bale.read', <LotPerformancePage />)) },
      { path: 'bale-mgmt/bale-ageing',       element: bm(rp('bale.read', <BaleAgeingPage />)) },
      { path: 'bale-mgmt/mix-recipe-picker', element: bm(rp('bale.read', <MixRecipePickerPage />)) },
      { path: 'bale-mgmt/laydown-planning',  element: bm(rp('bale.read', <LaydownPlanningPage />)) },
      { path: 'bale-mgmt/pre-conditioning',  element: bm(rp('bale.read', <PreConditioningPage />)) },
      { path: 'bale-mgmt/yarn-recipes',      element: bm(rp('recipe.read', <YarnRecipesPage />)) },
      { path: 'bale-mgmt/waste-categories',  element: bm(rp('waste.read', <WasteCategoriesPage />)) },
      { path: 'bale-mgmt/waste-stock',       element: bm(rp('waste.read', <WasteStockPage />)) },
      { path: 'bale-mgmt/stage-costs',       element: bm(rp('waste.read', <StageCostsPage />)) },
      { path: 'bale-mgmt/laydown-execution', element: bm(rp('bale.read', <LaydownExecutionPage />)) },
      { path: 'bale-mgmt/chute-feed',        element: bm(rp('bale.read', <ChuteFeedPage />)) },
      // Sprint Costing — daily/variance reports + lazy re-weigh capture
      { path: 'bale-mgmt/daily-mixing-cost', element: bm(rp('bale.read', <DailyMixingCostPage />)) },
      { path: 'bale-mgmt/cost-variance',     element: bm(rp('bale.read', <CostVariancePage />)) },
      { path: 'bale-mgmt/pending-reweigh',   element: bm(rp('bale.read', <PendingReweighPage />)) },
      // Floor Chart — print-ready bale laydown sheet (replaces the paper one).
      // Wrapped in 'bm' frame so the tagline + sidebar still show on screen,
      // but the FloorChartPage's own print CSS strips them when printing.
      { path: 'bale-mgmt/laydowns/:ld_id/floor-chart',
        element: bm(rp('bale.read', <FloorChartPage />)) },
      // Bales Runout Report — for GM/QC review. Auto-generated when a
      // mill lot's last bale is consumed; shows purchase-vs-actual
      // deltas and flags supplier/handling-loss anomalies.
      { path: 'bale-mgmt/bales-runout-report',
        element: bm(rp('bale.read', <BalesRunoutReportPage />)) },
      // Sprint 0072 — Reconciliation panel: issued kg vs cards-consumed kg
      // per laydown. Auto-closes within tolerance, flags variances, and
      // gives supervisor a force-close path with audit trail.
      { path: 'bale-mgmt/reconciliation',
        element: bm(rp('bale.read', <LaydownReconciliationPage />)) },

      // ── HR & Payroll ───────────────────────────────────────────────────────
      { path: 'hr/employees',          element: rp('employee.read',   <EmployeesPage />) },
      // Full tabbed profile editor — Attendance, Salary, Bank, Statutory,
      // Skill, Nominee, Documents, etc. Reuses the joining tab components.
      { path: 'hr/employees/:id',      element: rp('employee.read',   <EmployeeDetailPage />) },
      // Sprint 0081 — Skill / Trade Matrix
      { path: 'hr/skill-matrix',       element: rp('employee.read',   <SkillMatrixPage />) },
      // Sprint 0082 — Manpower allocation chain
      { path: 'hr/sanctioned-strength', element: rp('employee.read',   <SanctionedStrengthPage />) },
      { path: 'hr/manpower-norms',      element: rp('employee.read',   <ManpowerNormsPage />) },
      { path: 'hr/worker-roles',        element: rp('employee.read',   <WorkerRoleMasterPage />) },
      { path: 'hr/worker-allocation',   element: rp('employee.read',   <WorkerAllocationPage />) },
      // Sprint 0144 ATT — GM approvals for no-gate-log allocation overrides
      { path: 'hr/allocation-gm-approvals',   element: rp('employee.create',   <AllocationGMApprovalsPage />) },
      // Sprint 0083 — full implementation readiness
      { path: 'hr/config-completeness', element: rp('employee.read',   <ConfigCompletenessPage />) },
      { path: 'hr/punch-logs',          element: rp('employee.read',   <PunchLogsPage />) },
      { path: 'hr/daily-attendance-report', element: rp('employee.read', <DailyAttendanceReportPage />) },
      { path: 'hr/biometric-devices',   element: rp('employee.read',   <BiometricDevicesPage />) },
      // Phase A — Welfare masters
      { path: 'hr/colonies-rooms',      element: rp('employee.read',   <ColoniesRoomsPage />) },
      { path: 'hr/canteen-rules',       element: rp('employee.read',   <CanteenRulesPage />) },
      { path: 'hr/ration-rules',        element: rp('employee.read',   <RationRulesPage />) },
      { path: 'hr/welfare-approvals',   element: rp('employee.update', <WelfareApprovalsInboxPage />) },
      { path: 'hr/ration-advances',     element: rp('employee.read',   <RationAdvancesPage />) },
      // Employee Joining Workflow
      { path: 'hr/joining',             element: rp('employee.read',   <JoiningListPage />) },
      { path: 'hr/joining/:id',         element: rp('employee.read',   <JoiningDetailPage />) },
      // PJD-001 Task 11 — Document Grace watchlist (2nd-ID chase ladder)
      { path: 'hr/doc-grace',           element: rp('hr.doc_grace.read', <DocGraceWatchlistPage />) },
      // Contractor Foundation — contractor master + detail
      { path: 'hr/contractors',         element: rp('employee.read',   <ContractorsPage />) },
      { path: 'hr/contractors/:id',     element: rp('employee.read',   <ContractorDetailPage />) },
      // Contractor Headcount SLA — committed-vs-active shortfall tasks
      { path: 'hr/contractor-headcount-sla', element: rp('contractor.read', <ContractorHeadcountSlaTasksPage />) },
      // Food Store — welfare food catalog + batch expiry + FEFO issue dashboard
      { path: 'hr/food-store',          element: rp('foodstore.read',  <FoodStoreDashboardPage />) },
      { path: 'hr/food-store/items',    element: rp('foodstore.read',  <FoodItemsPage />) },
      { path: 'hr/food-store/issue',    element: rp('foodstore.read',  <FoodIssuePage />) },
      { path: 'hr/food-store/batches',  element: rp('foodstore.read',  <FoodBatchesPage />) },
      { path: 'hr/food-store/contractor-ledger', element: rp('foodstore.read', <ContractorFoodLedgerPage />) },
      // Attendance Variance Tasks — HR → GM → MD escalation
      { path: 'hr/attendance-variance', element: rp('employee.read',   <AttendanceVarianceTasksPage />) },
      // Skill Mismatch system — HR → GM → MD escalation + bulk classify
      { path: 'hr/worker-skills',       element: rp('employee.read',   <WorkerSkillsPage />) },
      { path: 'hr/skill-mismatch',      element: rp('employee.read',   <SkillMismatchTasksPage />) },
      // Separation / Resignation workflow + F&F settlement
      { path: 'hr/separations',         element: rp('employee.read',   <SeparationListPage />) },
      { path: 'hr/separations/:id',     element: rp('employee.read',   <SeparationDetailPage />) },
      // Employee ledgers — loans / canteen / colony bills
      { path: 'hr/employee-ledgers',    element: rp('employee.read',   <EmployeeLedgersPage />) },
      // Statutory Exit dashboard — PF / ESI / Gratuity
      { path: 'hr/statutory-exit',      element: rp('employee.read',   <StatutoryExitDashboardPage />) },
      // Phase 5+6+7 — Asset ledger / unified reports / F&F finance posting
      { path: 'hr/employee-assets',     element: rp('employee.read',   <EmployeeAssetsPage />) },
      { path: 'hr/separation-reports',  element: rp('employee.read',   <SeparationReportsPage />) },
      { path: 'hr/finance-posting',     element: rp('employee.read',   <FinancePostingPage />) },
      { path: 'hr/rule-config',         element: rp('employee.read',   <RuleConfigPage />) },
      // Phase 0084 — Role Responsibility Master
      { path: 'hr/role-master',         element: rp('employee.read',   <RoleMasterListPage />) },
      { path: 'hr/role-master/:roleCode', element: rp('employee.read', <RoleDetailPage />) },
      { path: 'hr/org-chart',           element: rp('employee.read',   <OrgChartPage />) },
      { path: 'hr/reporting-hierarchy-audit', element: rp('employee.read', <ReportingHierarchyAuditPage />) },
      // Phase 0085 — Task execution + scoring + awards
      { path: 'hr/my-todo',             element: rp('employee.read',   <MyTodoPage />) },
      { path: 'hr/team-monitor',        element: rp('employee.read',   <TeamMonitorPage />) },
      { path: 'hr/verify-queue',        element: rp('employee.read',   <TaskVerificationPage />) },
      { path: 'hr/escalation-inbox',    element: rp('employee.read',   <EscalationInboxPage />) },
      // Phase HR-003 Wave 3 — Grievance Pattern Study (#9)
      { path: 'hr/grievances',          element: rp('grievance.read',  <GrievanceInboxPage />) },
      // Phase HR-003 Wave 4 — Worker Efficiency Study (#26)
      { path: 'hr/worker-efficiency',   element: rp('attendance.read', <WorkerEfficiencyPage />) },
      // Phase HR-003 Wave 5 — EHS Floor Walk (Studies #33 #35 #40 #41 #43)
      { path: 'hr/safety-walk',         element: rp('employee.read',   <SafetyWalkPage />) },
      // Phase HR-003 Wave 6 — Training (#8) + Production walk (#17 #18 #22)
      { path: 'hr/training',            element: rp('employee.read',   <TrainingPage />) },
      { path: 'hr/production-walk',     element: rp('employee.read',   <ProductionWalkPage />) },
      // Phase HR-003 Wave 7 — Welfare audit (Study #37)
      { path: 'hr/welfare',             element: rp('welfare.read',    <WelfarePage />) },
      // Phase HR-004 Wave 2 — Attendance 3-way reconciliation
      { path: 'hr/reconciliation',      element: rp('attendance.read', <AttendanceReconciliationPage />) },
      { path: 'hr/task-calendar',       element: rp('employee.read',   <TaskCalendarPage />) },
      { path: 'hr/awards',              element: rp('employee.read',   <AwardsPage />) },
      // Phase 0085-B — Assessment engine
      { path: 'hr/assessment-templates', element: rp('employee.read',  <AssessmentTemplatesPage />) },
      { path: 'hr/assessment-cycles',    element: rp('employee.read',  <AssessmentCyclesPage />) },
      { path: 'hr/my-assessments',       element: rp('employee.read',  <MyAssessmentsPage />) },
      { path: 'hr/assessments/:eaId',    element: rp('employee.read',  <AssessmentDetailPage />) },
      // Phase 0088 — Scheduler admin
      { path: 'hr/scheduler',            element: rp('employee.read',  <SchedulerAdminPage />) },
      // Phase 0089 — Live shop-floor studies
      { path: 'hr/live-studies',         element: rp('employee.read',  <LiveStudyHomePage />) },
      { path: 'hr/live-studies/start',   element: rp('employee.read',  <StartStudyWizardPage />) },
      { path: 'hr/live-studies/sessions/:sessionId', element: rp('employee.read', <LiveStudyCaptureRouter />) },
      { path: 'hr/live-studies/history', element: rp('employee.read',  <LiveStudyHistoryPage />) },
      // Phase 0091 — Automation control surface
      { path: 'hr/automation',           element: rp('employee.read',  <HRAutomationDashboardPage />) },
      { path: 'hr/default-config',       element: rp('employee.read',  <HRDefaultConfigPage />) },
      { path: 'hr/study-directions',     element: rp('employee.read',  <StudyDirectionsPage />) },
      // Phase 0094-P1 — Payroll
      { path: 'payroll/run',              element: rp('employee.read',  <PayrollRunPage />) },
      { path: 'payroll/salary-structures', element: rp('employee.read',  <SalaryStructuresPage />) },
      { path: 'payroll/salary-register',  element: rp('employee.read',  <SalaryRegisterPage />) },
      { path: 'payroll/costing',          element: rp('employee.read',  <PayrollCostingPage />) },
      { path: 'payroll/epf',              element: rp('employee.read',  <EPFPage />) },
      // Phase COTTON-RM-002 Part B — Cotton ops UI
      { path: 'inventory/cotton-in-premises',         element: rp('store.read', <CottonInPremisesPage />) },
      { path: 'inventory/cotton-movement',            element: rp('store.read', <CottonMovementPage />) },
      { path: 'quality/moisture',                     element: rp('store.read', <MoisturePage />) },
      { path: 'inventory/bales/qr-print',             element: rp('store.read', <BaleQrPrintPage />) },
      { path: 'inventory/bales/scan',                 element: rp('store.read', <BaleScanPage />) },
      { path: 'inventory/bales/traceability/:baleId', element: rp('store.read', <BaleTraceabilityPage />) },
      { path: 'inventory/cotton-cost-adjustments',    element: rp('store.read', <CottonCostAdjustmentsPage />) },
      // Phase COTTON-RM-002 Part C — control surface
      { path: 'inventory/cotton-rm-dashboard',        element: rp('store.read', <CottonRmDashboardPage />) },
      { path: 'quality/cotton-qc-pending',            element: rp('quality.read', <CottonQcPendingPage />) },
      { path: 'inventory/mixing-readiness',           element: rp('store.read', <MixingReadinessReportPage />) },
      { path: 'inventory/cotton-cost-adjustment-report', element: rp('store.read', <CottonCostAdjustmentReportPage />) },
      { path: 'inventory/cotton-cost-restatements',   element: rp('store.read', <CottonCostRestatementsPage />) },
      { path: 'hr/attendance-derivation', element: rp('employee.read', <AttendanceDerivationPage />) },
      { path: 'hr/certification-expiry', element: rp('employee.read',  <CertificationExpiryPage />) },
      // Phase 0092 — HR Executive Dashboard
      { path: 'hr/executive-dashboard',  element: rp('employee.read',  <HRExecutiveDashboardPage />) },
      { path: 'hr/shift-roster',       element: rp('employee.read',   <ShiftRosterPage />) },
      { path: 'hr/attendance',         element: rp('attendance.read', <AttendancePage />) },
      { path: 'hr/leave-types',        element: rp('leave.read',      <LeaveTypesPage />) },
      { path: 'hr/leave-applications', element: rp('leave.read',      <LeaveApplicationsPage />) },
      { path: 'hr/leave-balances',     element: rp('leave.read',      <LeaveBalancesPage />) },
      { path: 'payroll/periods',       element: rp('payroll.read',    <PayrollPeriodPage />) },
      { path: 'payroll/payslips',      element: rp('payroll.read',    <PayslipPage />) },

      // ── FG & Sales ─────────────────────────────────────────────────────────
      { path: 'fg/store',        element: rp('fg.read',            <FGStorePage />) },
      { path: 'sales/customers', element: rp('customer.read',      <CustomerPage />) },
      { path: 'sales/orders',    element: rp('sales.read',         <SalesOrderPage />) },
      { path: 'sales/stock-build', element: rp('sales.read',       <StockBuildAuthPage />) },
      { path: 'sales/dispatch',  element: rp('dispatch.read',      <DispatchPage />) },
      { path: 'sales/invoices',  element: rp('sales_invoice.read', <InvoicePage />) },

      // ── Finance & Accounts ─────────────────────────────────────────────────
      { path: 'accounting',               element: rp('accounting.read', <AccountingDashboardPage />) },
      { path: 'accounting/coa',           element: rp('accounting.read', <ChartOfAccountsPage />) },
      { path: 'accounting/vouchers',      element: rp('voucher.read',    <VouchersPage />) },
      { path: 'accounting/gst',           element: rp('accounting.read', <GSTRegisterPage />) },
      { path: 'accounting/tds',           element: rp('accounting.read', <TDSRegisterPage />) },
      { path: 'accounting/brs',           element: rp('accounting.read', <BankReconciliationPage />) },
      { path: 'accounting/fixed-assets',  element: rp('accounting.read', <FixedAssetsPage />) },
      { path: 'accounting/compliance',    element: rp('accounting.read', <ComplianceDashboardPage />) },
      { path: 'accounting/trial-balance', element: rp('accounting.read', <TrialBalancePage />) },
      { path: 'accounting/monthly-close',    element: rp('accounting.read', <MonthlyClosePage />) },
      { path: 'accounting/standard-costs',   element: rp('accounting.read', <StandardCostPage />) },
      { path: 'accounting/audit-schedules',  element: rp('accounting.read', <AuditSchedulesPage />) },
      { path: 'accounting/ap-invoice-booking', element: rp('voucher.create', <APInvoiceBookingPage />) },
      { path: 'accounting/tally-import',       element: rp('voucher.create', <TallyImportPage />) },
      { path: 'accounting/bill-wise',          element: rp('accounting.read', <BillWisePage />) },
      { path: 'accounting/financial-reports',    element: rp('accounting.read', <FinancialReportsPage />) },
      { path: 'accounting/financial-statements', element: rp('accounting.read', <FinancialStatementsPage />) },
      { path: 'accounting/payment-allocation',   element: rp('voucher.create', <PaymentAllocationPage />) },

      // ── Approval Inbox (all authenticated users; empty for those with no approver role) ──
      { path: 'approvals/my-pending', element: <MyPendingApprovalsPage /> },
      { path: 'approvals/inbox', element: <ApprovalInboxPage /> },
      { path: 'approvals/vendors', element: rpAny(['supplier.approve_pm', 'supplier.approve_cfo'], <VendorApprovalInboxPage />) },

      // ── Maintenance (Sprint 0075 Phase 2) ──────────────────────────────────
      // Role-wise screens. The backend spine is shared — each route is a
      // different lens: technician sees their assigned jobs, supervisor sees
      // the board, store sees the spare desk, etc. Permissions:
      //   maintenance.read   — can see boards, calendar, masters, audit
      //   maintenance.create — can raise requests, generate PMs, plan shutdowns
      //   maintenance.update — can assign, complete, close, defer, approve
      { path: 'maintenance',                element: rp('maintenance.read',   <MaintenanceHomePage />) },
      { path: 'maintenance/requests',       element: rp('maintenance.read',   <BreakdownRequestPage />) },
      { path: 'maintenance/work-orders',    element: rp('maintenance.read',   <WorkOrderBoardPage />) },
      { path: 'maintenance/my-jobs',        element: rp('maintenance.read',   <TechnicianMyJobsPage />) },
      { path: 'maintenance/spares',         element: rpAny(['spares.read', 'maintenance.read'], <SpareIssuePage />) },
      { path: 'maintenance/pm',             element: rp('maintenance.read',   <PMCalendarPage />) },
      { path: 'maintenance/shutdown-plans', element: rp('maintenance.read',   <ShutdownPlanPage />) },
      { path: 'maintenance/utility-logs',   element: rp('maintenance.read',   <UtilityRunningLogPage />) },
      { path: 'maintenance/machine-health', element: rp('maintenance.read',   <MachineHealthPage />) },
      { path: 'maintenance/masters',        element: rp('maintenance.create', <MaintenanceMastersPage />) },
      // Sprint 0078 — auto-assignment rules
      { path: 'maintenance/assignment-rules', element: rp('maintenance.create', <AssignmentRulesPage />) },
      // Phase 3 — Dashboards + reports. All gated by maintenance.read.
      { path: 'maintenance/manager-dashboard',    element: rp('maintenance.read', <MaintenanceManagerDashboardPage />) },
      { path: 'maintenance/plant-head',           element: rp('maintenance.read', <PlantHeadMaintenanceDashboardPage />) },
      { path: 'maintenance/reports',              element: rp('maintenance.read', <MaintenanceReportsPage />) },
      { path: 'maintenance/reports/machine-downtime',       element: rp('maintenance.read', <MachineDowntimeReportPage />) },
      { path: 'maintenance/reports/department-breakdown',   element: rp('maintenance.read', <DepartmentBreakdownReportPage />) },
      { path: 'maintenance/reports/pm-compliance',          element: rp('maintenance.read', <PMComplianceReportPage />) },
      { path: 'maintenance/reports/spare-consumption',      element: rp('maintenance.read', <SpareConsumptionReportPage />) },
      { path: 'maintenance/reports/technician-performance', element: rp('maintenance.read', <TechnicianPerformanceReportPage />) },
      { path: 'maintenance/reports/cost',                   element: rp('maintenance.read', <MaintenanceCostReportPage />) },
      { path: 'maintenance/reports/repeat-breakdown',       element: rp('maintenance.read', <RepeatBreakdownReportPage />) },
      { path: 'maintenance/reports/pending-ageing',         element: rp('maintenance.read', <PendingWorkOrderAgeingReportPage />) },
      { path: 'maintenance/reports/utility-performance',    element: rp('maintenance.read', <UtilityPerformanceReportPage />) },
      { path: 'maintenance/reports/machine-health',         element: rp('maintenance.read', <MachineHealthAnalyticsPage />) },
      { path: 'maintenance/reports/cost-per-kg-trend',      element: rp('maintenance.read', <MaintenanceCostPerKgTrendPage />) },
      // Phase 3.2-B — Maintenance ↔ Procurement ↔ Accounts
      { path: 'maintenance/spare-procurement',              element: rp('maintenance.read',   <SpareProcurementDashboardPage />) },
      { path: 'maintenance/spare-procurement/auto-pr',      element: rp('maintenance.create', <AutoPRFromWorkOrderPage />) },
      { path: 'maintenance/spare-procurement/status',       element: rp('maintenance.read',   <MaintenanceProcurementStatusPage />) },
      { path: 'maintenance/spare-procurement/wo/:wo_id',    element: rp('maintenance.read',   <MaintenanceProcurementStatusPage />) },
      { path: 'maintenance/spare-procurement/grn',          element: rpAny(['grn.create', 'maintenance.update'], <SpareGRNEntryPage />) },
      { path: 'maintenance/spare-procurement/invoice',      element: rpAny(['accounting.read', 'maintenance.read'], <SpareInvoiceMatchPage />) },
      { path: 'maintenance/spare-procurement/advance',      element: rpAny(['accounting.read', 'maintenance.read'], <POAdvancePaymentPage />) },

      // ── Inventory Ageing (Sprint 0076 Phase 3.2-C) ─────────────────────────
      // Backend-driven ageing buckets + slow-moving + dead-stock + critical
      // reserved exception. Frontend renders cards, tables, badges — no math.
      { path: 'inventory/ageing',
        element: rp('store.read', <StockAgeingDashboardPage />) },
      { path: 'inventory/ageing/item-wise',
        element: rp('store.read',
          <AgeingReportPage flavour="item-wise" title="Item-wise Ageing"
            subtitle="One row per spare with backend-aggregated lot ageing." />) },
      { path: 'inventory/ageing/lot-wise',
        element: rp('store.read',
          <AgeingReportPage flavour="lot-wise" title="Lot-wise Ageing"
            subtitle="Per-lot FIFO ageing with landed-rate valuation." />) },
      { path: 'inventory/ageing/slow-moving',
        element: rp('store.read',
          <AgeingReportPage flavour="slow-moving" title="Slow-Moving Stock"
            subtitle="Items with no issue in 90+ days (excludes critical reserved)." />) },
      { path: 'inventory/ageing/dead-stock',
        element: rp('store.read',
          <AgeingReportPage flavour="dead-stock" title="Dead Stock"
            subtitle="No issue in 180+ days. Critical spares routed to Critical Reserved." />) },
      { path: 'inventory/ageing/critical',
        element: rp('store.read',
          <AgeingReportPage flavour="critical" title="Critical Spare Ageing"
            subtitle="Critical spares — old but reserved by design, never dead." />) },
      { path: 'inventory/ageing/high-value',
        element: rp('store.read',
          <AgeingReportPage flavour="high-value" title="High-Value Old Stock"
            subtitle="High-₹ items older than 180 days. Disposal candidates." />) },
      { path: 'inventory/ageing/machine-wise',
        element: rp('store.read',
          <AgeingReportPage flavour="machine-wise" title="Machine-wise Spare Ageing"
            subtitle="Per-machine spare lot age + 90/180-day usage." />) },

      // ── MIS & Reports ──────────────────────────────────────────────────────
      { path: 'mis/dashboard',          element: rp('mis.read', <MISDashboardPage />) },
      { path: 'mis/profit-loss',        element: rp('mis.read', <ProfitLossPage />) },
      { path: 'mis/balance-sheet',      element: rp('mis.read', <BalanceSheetPage />) },
      { path: 'mis/debtor-aging',       element: rp('mis.read', <DebtorAgingPage />) },
      { path: 'mis/creditor-aging',     element: rp('mis.read', <CreditorAgingPage />) },
      { path: 'mis/cost-of-production', element: rp('mis.read', <CostOfProductionPage />) },
      { path: 'mis/budget',             element: rp('mis.read', <BudgetPage />) },
      { path: 'mis/cash-flow',          element: rp('mis.read', <CashFlowPage />) },
      { path: 'mis/landed-cost',        element: rp('mis.read', <LandedCostPage />) },

      // ── MIS-001 Slice 1 — Executive Dashboard + Insights Reports ────────────
      { path: 'mis/executive', element: rp('mis.executive.read', <ExecutiveDashboardPage />) },
      { path: 'mis/reports',   element: rp('mis.reports.read',   <InsightsReportsPage />) },

      // ── MIS-001 Slice 2 — Stores Cockpit + Maintenance Cockpit ──────────────
      { path: 'mis/stores',      element: rp('mis.stores.read',      <StoresCockpitPage />) },
      { path: 'mis/maintenance', element: rp('mis.maintenance.read', <MaintenanceCockpitPage />) },

      // ── BI-001 Slice 1 — Custom dashboard list + viewer ─────────────────────
      { path: 'bi/dashboards',     element: rp('mis.dashboard.view',  <DashboardListPage />) },
      { path: 'bi/dashboards/new', element: rp('mis.dashboard.build', <DashboardBuilderPage />) },
      { path: 'bi/dashboards/:id', element: rp('mis.dashboard.view',  <DashboardViewPage />) },
      { path: 'bi/dashboards/:id/edit', element: rp('mis.dashboard.build', <DashboardBuilderPage />) },

      // ── GOV-001 P4: cost rate change approval ──────────────────────────────
      { path: 'costing/rate-change-requests', element: rp('cost.rate.request.view', <CostRateChangeRequestsPage />) },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
])

export default function Router() {
  return <RouterProvider router={router} />
}
